import { useRouteMatch } from "react-router-dom";
import { BASE_PUBLIC_BOT_PATH } from "common/constants";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import PublicBotSummary from "features/bots/PublicBots/PublicBotSummary";
import {
  BotGraphType,
  BotType,
  LanguageNameSpaces,
  QueryBotType,
  WorkspaceType,
} from "@shared/types";
import BotChart from "features/bots/common/BotChart";
import PublicBotStatistics from "features/bots/PublicBots/PublicBotStatistics";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Tabs from "common/Tabs/Tabs";
import useMedia from "utils/hooks/useMedia";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import { PublicBotSummaryTabs } from "@shared/features/bots/types";
import { usePublicBotPage } from "@shared/features/bots/hooks";
import BotFormProvider from "@shared/features/bots/BotForm/common/BotFormProvider";
import {
  ENV_TYPE_QUERY_PARAM,
  QUERY_BOT_TYPE_TO_BOT_TYPE,
  TIME_RANGE_START,
} from "@shared/common/constants";
import InvalidPublicBotModal from "features/bots/PublicBots/InvalidPublicBotModal";
import useQuery from "utils/hooks/useQuery";
import PageTabs from "common/Tabs/PageTabs.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import React from "react";
import Card from "common/Boxes/Card.tsx";
import {
  phoneMaxWidth,
  phoneMinWidth,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout";
import { NEW_LOOK_GAP } from "common/NewLook/tokens.ts";
import NewLookPublicBotPerformance from "features/bots/PublicBots/NewLookPublicBotPerformance.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const PublicBotPreview = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: 860px)`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const publicBotMatch = useRouteMatch(BASE_PUBLIC_BOT_PATH);
  const query = useQuery();
  const isDemoCopyPublic =
    query.get(ENV_TYPE_QUERY_PARAM) === WorkspaceType.DEMO;
  const { botType: queryBotType, botUUID } = (publicBotMatch?.params || {}) as {
    botType?: string;
    botUUID?: string;
  };

  const botType: BotType =
    QUERY_BOT_TYPE_TO_BOT_TYPE[queryBotType || QueryBotType.ASAP];
  const showStatisticsRow = [BotType.ASAP, BotType.GRID].includes(botType);

  const {
    pageTab,
    setPageTab,
    chartTab,
    setChartTab,
    getBotDataStatus,
    publicBotData,
    publicBotErrors,
  } = usePublicBotPage(botUUID, isDemoCopyPublic);

  const getCharts = () => {
    if (!botUUID) return;

    const accumulatedProfitChart = (
      <BotChart
        title={
          isMobile ? undefined : (
            <Title level={2} style={{ marginBottom: 8 }}>
              {t("public_bots.accumulated_profit")}
            </Title>
          )
        }
        publicBotUUID={botUUID}
        chartType={BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE}
        mobileBreakpoint={isMobile ? Infinity : undefined}
        defaultTimeRange={TIME_RANGE_START}
        isDemoCopyPublic={isDemoCopyPublic}
      />
    );
    const profitByDayChart = (
      <BotChart
        title={
          isMobile ? undefined : (
            <Title level={2} style={{ marginBottom: 8 }}>
              {t("public_bots.profit_over_time")}
            </Title>
          )
        }
        publicBotUUID={botUUID}
        chartType={BotGraphType.PROFIT_BY_DAY_PERCENTAGE}
        mobileBreakpoint={isMobile ? Infinity : undefined}
        defaultTimeRange={TIME_RANGE_START}
        isDemoCopyPublic={isDemoCopyPublic}
      />
    );

    const graphTabsItems = [
      {
        key: BotGraphType.ACCUMULATING_PROFIT.toString(),
        label: t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT]),
        children: <ChartWrapper>{accumulatedProfitChart}</ChartWrapper>,
      },
      {
        key: BotGraphType.PROFIT_BY_DAY.toString(),
        label: t(BotGraphTypeToDisplay[BotGraphType.PROFIT_BY_DAY]),
        children: <ChartWrapper>{profitByDayChart}</ChartWrapper>,
      },
    ];

    if (!isMobile) {
      return (
        <>
          <StatisticCardWrapper>
            <ChartWrapper>{accumulatedProfitChart}</ChartWrapper>
          </StatisticCardWrapper>
          <StatisticCardWrapper>
            <ChartWrapper>{profitByDayChart}</ChartWrapper>
          </StatisticCardWrapper>
        </>
      );
    } else {
      return (
        <StatisticCardWrapper style={{ padding: "8px 16px 16px 16px" }}>
          <Tabs
            activeKey={chartTab.toString()}
            onTabClick={(tabKey: string) => setChartTab(parseInt(tabKey))}
            size="small"
            tabBarStyle={{
              margin: "0 0 8px 0",
            }}
            $hideBorder={true}
            items={graphTabsItems}
          />
        </StatisticCardWrapper>
      );
    }
  };

  const botStatistics = (
    <ContentWrapper>
      {botUUID && (
        <>
          {showStatisticsRow && (
            <StatisticCardWrapper style={{ height: 90 }}>
              <LoadingSpinnerOverlay
                isLoading={getBotDataStatus.isFetching}
                backgroundColor={theme.colors.cardBgColor}
              >
                {publicBotData && (
                  <PublicBotStatistics
                    botData={publicBotData}
                    created={getBotDataStatus?.data?.created_at}
                    showBorder={true}
                  />
                )}
              </LoadingSpinnerOverlay>
            </StatisticCardWrapper>
          )}
          {getCharts()}
        </>
      )}
    </ContentWrapper>
  );

  const summary = botType && (
    <BotFormProvider
      botFormType={BotFormType.COPY_PUBLIC}
      botType={botType}
      botUUID={botUUID}
      isDemoCopyPublic={isDemoCopyPublic}
    >
      <PublicBotSummary botUUID={botUUID} />
    </BotFormProvider>
  );

  const tabsItems = [
    {
      key: PublicBotSummaryTabs.SUMMARY,
      label: t("public_bots.summary"),
      children: summary,
    },
    {
      key: PublicBotSummaryTabs.STATISTICS,
      label: t("public_bots.statistics"),
      children: botStatistics,
    },
  ];

  const tabs = (
    <Tabs
      activeKey={pageTab}
      onTabClick={(tab: string) => setPageTab(tab as PublicBotSummaryTabs)}
      size="small"
      tabBarStyle={{
        margin: "0 0 12px 0",
      }}
      items={tabsItems}
    />
  );

  if (theme.isNewLook) {
    // New look: the bot's results (the page's lead) and the copy form as
    // panels side by side; stacked on tablets, as tabs on phones.
    const performance = botUUID && (
      <NewLookPublicBotPerformance
        botUUID={botUUID}
        botData={publicBotData}
        created={getBotDataStatus.data?.created_at}
        isDemoCopyPublic={isDemoCopyPublic}
        showIdentity={true}
      />
    );
    const newLookContent = isBottomNavigator ? (
      <PageTabs
        items={[
          tabsItems[0],
          {
            ...tabsItems[1],
            children: <NewLookPhonePanel>{performance}</NewLookPhonePanel>,
          },
        ]}
      />
    ) : (
      <NewLookLayout>
        <NewLookSummary>{summary}</NewLookSummary>
        <NewLookPerformance>{performance}</NewLookPerformance>
      </NewLookLayout>
    );
    return (
      <>
        {newLookContent}
        <InvalidPublicBotModal publicBotErrors={publicBotErrors} />
      </>
    );
  }

  const getContent = () => {
    if (isBottomNavigator) {
      return <PageTabs items={tabsItems} />;
    } else if (isMobile) {
      return tabs;
    }
    return (
      <>
        {summary}
        {botStatistics}
      </>
    );
  };

  return (
    <Wrapper $isMobile={isMobile}>
      {getContent()}
      <InvalidPublicBotModal publicBotErrors={publicBotErrors} />
    </Wrapper>
  );
};

export default PublicBotPreview;

const Wrapper = styled.div<{ $isMobile: boolean }>`
  display: grid;
  padding: 20px;
  column-gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
  grid-template-columns: minmax(410px, 35%) 1fr;
  ${(p) =>
    p.$isMobile &&
    `
    display: flex;
    flex-direction: column;
    padding: 12px;
  `};
  @media (max-width: ${phoneMaxWidth}) {
    margin: 0;
    padding: 0;
  }
`;
const NewLookLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 34%) minmax(0, 1fr);
  grid-template-areas: "summary performance";
  align-items: start;
  gap: ${NEW_LOOK_GAP}px;
  width: 100%;
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_PAGE_GUTTER_VAR};
  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "performance"
      "summary";
  }
  @media (max-width: 600px) {
    padding: 16px;
  }
`;
const NewLookSummary = styled.div`
  grid-area: summary;
  min-width: 0;
`;
const NewLookPerformance = styled.div`
  grid-area: performance;
  min-width: 0;
`;
const NewLookPhonePanel = styled.div`
  padding: 16px;
`;
const ContentWrapper = styled.div`
  overflow: hidden;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 12px;
  }
`;
const StatisticCardWrapper = styled(Card)`
  margin-bottom: 16px;
  width: 100%;
  display: inline-grid;
  padding: 12px;
  position: relative;
  @media (min-width: ${phoneMinWidth}) {
    border: ${(p) => p.theme.borders.medium};
    box-shadow: none;
    align-items: center;
  }
`;
const ChartWrapper = styled.div`
  height: 290px;
  width: 100%;
`;
